import React, { useState, useEffect } from 'react';
import { Card, Form, Button, Alert, Spinner, Modal } from 'react-bootstrap';
import {
  getIntegrationsUsps,
  postIntegrationsUsps,
  deleteIntegrationsUspsIntegrationId,
  postIntegrationsUspsIntegrationIdTestConnection,
  putIntegrationsUspsIntegrationId,
} from '../../api/generated/arrayscapeGamingDashboardAPI';

const USPSTab: React.FC = () => {
  const [uspsIntegrations, setUspsIntegrations] = useState<any[]>([]);
  const [isLoadingUspsIntegrations, setIsLoadingUspsIntegrations] = useState(false);
  const [uspsError, setUspsError] = useState<string | null>(null);
  const [showAddUspsIntegration, setShowAddUspsIntegration] = useState(false);
  const [showEditUspsIntegration, setShowEditUspsIntegration] = useState(false);
  const [editingUspsIntegration, setEditingUspsIntegration] = useState<any | null>(null);
  const [newUspsName, setNewUspsName] = useState('');
  const [newUspsClientId, setNewUspsClientId] = useState('');
  const [newUspsClientSecret, setNewUspsClientSecret] = useState('');
  const [newUspsDebug, setNewUspsDebug] = useState(false);
  const [showUspsClientSecret, setShowUspsClientSecret] = useState(false);
  const [isCreatingUsps, setIsCreatingUsps] = useState(false);

  // Fetch integrations on mount
  useEffect(() => {
    fetchUspsIntegrations();
  }, []);

  const fetchUspsIntegrations = async () => {
    setIsLoadingUspsIntegrations(true);
    setUspsError(null);
    try {
      const response = await getIntegrationsUsps();
      setUspsIntegrations(response.integrations || []);
    } catch (error) {
      console.error('Error fetching USPS integrations:', error);
      setUspsError('Failed to fetch USPS integrations');
    } finally {
      setIsLoadingUspsIntegrations(false);
    }
  };

  const handleCreateUspsIntegration = async () => {
    setIsCreatingUsps(true);
    setUspsError(null);
    try {
      if (!newUspsName.trim()) {
        setUspsError('Please enter a name for this integration');
        setIsCreatingUsps(false);
        return;
      }
      if (!newUspsClientId.trim()) {
        setUspsError('Please enter a USPS Client ID');
        setIsCreatingUsps(false);
        return;
      }
      if (!newUspsClientSecret.trim()) {
        setUspsError('Please enter a USPS Client Secret');
        setIsCreatingUsps(false);
        return;
      }

      await postIntegrationsUsps({
        name: newUspsName,
        client_id: newUspsClientId,
        client_secret: newUspsClientSecret,
        debug: newUspsDebug ? 'debug' : undefined
      });

      setShowAddUspsIntegration(false);
      setNewUspsName('');
      setNewUspsClientId('');
      setNewUspsClientSecret('');
      setNewUspsDebug(false);
      await fetchUspsIntegrations();
    } catch (error: any) {
      console.error('Error creating USPS integration:', error);
      if (error.response?.status === 400) {
        setUspsError('Invalid credentials or a USPS integration already exists (limit: 1)');
      } else {
        setUspsError('Failed to create USPS integration');
      }
    } finally {
      setIsCreatingUsps(false);
    }
  };

  const handleEditUspsIntegration = (integration: any) => {
    setEditingUspsIntegration(integration);
    setNewUspsName(integration.name || '');
    setNewUspsClientId('');
    setNewUspsClientSecret('');
    setNewUspsDebug(integration.debug === 'debug');
    setShowEditUspsIntegration(true);
  };

  const handleUpdateUspsIntegration = async () => {
    if (!editingUspsIntegration) return;

    setIsCreatingUsps(true);
    setUspsError(null);
    try {
      if (!newUspsName.trim()) {
        setUspsError('Please enter a name for this integration');
        setIsCreatingUsps(false);
        return;
      }
      if (!newUspsClientId.trim()) {
        setUspsError('Please enter a USPS Client ID');
        setIsCreatingUsps(false);
        return;
      }
      if (!newUspsClientSecret.trim()) {
        setUspsError('Please enter a USPS Client Secret');
        setIsCreatingUsps(false);
        return;
      }

      await putIntegrationsUspsIntegrationId(editingUspsIntegration.id, {
        name: newUspsName,
        client_id: newUspsClientId,
        client_secret: newUspsClientSecret,
        debug: newUspsDebug ? 'debug' : undefined
      });

      setShowEditUspsIntegration(false);
      setEditingUspsIntegration(null);
      setNewUspsName('');
      setNewUspsClientId('');
      setNewUspsClientSecret('');
      setNewUspsDebug(false);
      await fetchUspsIntegrations();
    } catch (error: any) {
      console.error('Error updating USPS integration:', error);
      if (error.response?.status === 400) {
        setUspsError('Invalid credentials or name already exists');
      } else {
        setUspsError('Failed to update USPS integration');
      }
    } finally {
      setIsCreatingUsps(false);
    }
  };

  const handleDeleteUspsIntegration = async (integrationId: string, name: string) => {
    if (!window.confirm(`Are you sure you want to delete the USPS integration "${name}"?`)) {
      return;
    }

    try {
      await deleteIntegrationsUspsIntegrationId(integrationId);
      await fetchUspsIntegrations();
    } catch (error) {
      console.error('Error deleting USPS integration:', error);
      setUspsError('Failed to delete USPS integration');
    }
  };

  const handleTestUspsConnection = async (integrationId: string, name: string) => {
    try {
      await postIntegrationsUspsIntegrationIdTestConnection(integrationId);
      await fetchUspsIntegrations();
    } catch (error: any) {
      console.error('Error testing USPS connection:', error);
      await fetchUspsIntegrations();
    }
  };

  return (
    <>
      <Card className="">
        <Card.Body>
          <div className="d-flex align-items-center justify-content-between mb-4">
            <div className="d-flex align-items-center">
              <div className="ms-4 me-4 bg-header-control rounded p-2">
                <img src="/usps-logo.svg" alt="USPS" style={{ height: '4rem' }} />
              </div>
              <div>
                <h5 className="mb-1">USPS Integration</h5>
                <p className="text-muted mb-0">Configure USPS address validation API (limit: 1 integration)</p>
              </div>
            </div>
            {uspsIntegrations.length === 0 && (
              <Button
                variant="primary"
                onClick={() => setShowAddUspsIntegration(true)}
                disabled={isCreatingUsps}
              >
                <i className="bi bi-plus-circle me-1"></i>
                Add Integration
              </Button>
            )}
          </div>

          {uspsError && !showAddUspsIntegration && !showEditUspsIntegration && (
            <Alert variant="danger" dismissible onClose={() => setUspsError(null)}>
              {uspsError}
            </Alert>
          )}

          {isLoadingUspsIntegrations ? (
            <div className="text-center py-4">
              <Spinner animation="border" />
              <p className="mt-2 text-muted">Loading integration...</p>
            </div>
          ) : uspsIntegrations.length === 0 ? (
            <div className="text-center py-5">
              <div className="d-inline-block bg-header-control rounded p-2">
                <img src="/usps-logo.svg" alt="USPS" style={{ height: '4rem' }} />
              </div>
              <h6 className="mt-3 text-muted">No USPS Integration</h6>
              <p className="text-muted">Add a USPS integration to enable address validation</p>
            </div>
          ) : (
            <div className="row">
              {uspsIntegrations.map((integration) => (
                <div key={integration.id} className="col-md-12 mb-3">
                  <Card className="border">
                    <Card.Body>
                      <div className="d-flex justify-content-between align-items-start mb-3">
                        <div className="flex-grow-1">
                          <h6 className="mb-1">
                            {integration.name || 'USPS Integration'}
                          </h6>
                          <div className="text-muted small mb-1">
                            <i className="bi bi-key me-1"></i>
                            Client ID configured
                          </div>
                          {integration.debug === 'debug' && (
                            <div className="text-muted small">
                              <i className="bi bi-bug me-1"></i>
                              Debug mode enabled
                            </div>
                          )}
                        </div>
                        <span className={`badge ${
                          integration.connection_status === 'connected' ? 'bg-success' :
                          integration.connection_status === 'error' ? 'bg-danger' : 'bg-secondary'
                        }`}>
                          {integration.connection_status === 'connected' ? 'Connected' :
                           integration.connection_status === 'error' ? 'Error' : 'Not Tested'}
                        </span>
                      </div>

                      <div className="d-flex gap-2 mb-3">
                        <Button
                          variant="outline-success"
                          size="sm"
                          onClick={() => handleTestUspsConnection(integration.id, integration.name)}
                        >
                          <i className="bi bi-check-circle me-1"></i>
                          Test Connection
                        </Button>
                        <Button
                          variant="outline-primary"
                          size="sm"
                          onClick={() => handleEditUspsIntegration(integration)}
                        >
                          <i className="bi bi-pencil me-1"></i>
                          Edit
                        </Button>
                        <Button
                          variant="outline-danger"
                          size="sm"
                          onClick={() => handleDeleteUspsIntegration(integration.id, integration.name)}
                        >
                          <i className="bi bi-trash me-1"></i>
                          Delete
                        </Button>
                      </div>

                      {integration.connection_message && (
                        <div className={`alert ${integration.connection_status === 'connected' ? 'alert-success' : 'alert-danger'} alert-sm mb-3`} role="alert">
                          <i className={`bi ${integration.connection_status === 'connected' ? 'bi-check-circle' : 'bi-exclamation-triangle'} me-1`}></i>
                          <small>{integration.connection_message}</small>
                        </div>
                      )}

                      {integration.last_test_at && (
                        <div className="mb-0 pt-2 border-top">
                          <small className="text-muted d-block">Last Test:</small>
                          <small>
                            {new Date(integration.last_test_at).toLocaleString()}
                          </small>
                        </div>
                      )}
                    </Card.Body>
                  </Card>
                </div>
              ))}
            </div>
          )}
        </Card.Body>
      </Card>

      {/* Add USPS Integration Modal */}
      <Modal show={showAddUspsIntegration} onHide={() => {
        setShowAddUspsIntegration(false);
        setNewUspsName('');
        setNewUspsClientId('');
        setNewUspsClientSecret('');
        setNewUspsDebug(false);
        setUspsError(null);
        setShowUspsClientSecret(false);
      }} centered>
        <Modal.Header closeButton>
          <Modal.Title>Add New USPS Integration</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {uspsError && (
            <Alert variant="danger" dismissible onClose={() => setUspsError(null)}>
              {uspsError}
            </Alert>
          )}
          <Form.Group className="mb-3">
            <Form.Label>Integration Name <span className="text-danger">*</span></Form.Label>
            <Form.Control
              type="text"
              placeholder="e.g., USPS Production"
              value={newUspsName}
              onChange={(e) => setNewUspsName(e.target.value)}
              maxLength={255}
            />
            <Form.Text className="text-muted">
              Friendly name to identify this integration
            </Form.Text>
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>USPS Client ID <span className="text-danger">*</span></Form.Label>
            <Form.Control
              type="text"
              placeholder="Enter your USPS API Client ID"
              value={newUspsClientId}
              onChange={(e) => setNewUspsClientId(e.target.value)}
              style={{ fontFamily: 'monospace' }}
            />
            <Form.Text className="text-muted">
              Your USPS API Client ID. This will be encrypted and stored securely.
            </Form.Text>
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>USPS Client Secret <span className="text-danger">*</span></Form.Label>
            <div className="input-group">
              <Form.Control
                type={showUspsClientSecret ? "text" : "password"}
                placeholder="Enter your USPS API Client Secret"
                value={newUspsClientSecret}
                onChange={(e) => setNewUspsClientSecret(e.target.value)}
                style={{ fontFamily: 'monospace' }}
              />
              <Button
                variant="outline-secondary"
                onClick={() => setShowUspsClientSecret(!showUspsClientSecret)}
              >
                <i className={`bi ${showUspsClientSecret ? 'bi-eye-slash' : 'bi-eye'}`}></i>
              </Button>
            </div>
            <Form.Text className="text-muted">
              <i className="bi bi-shield-lock me-1"></i>
              Your USPS API Client Secret. This will be encrypted and stored securely.
            </Form.Text>
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Check
              type="checkbox"
              label="Enable Debug Mode"
              checked={newUspsDebug}
              onChange={(e) => setNewUspsDebug(e.target.checked)}
            />
            <Form.Text className="text-muted">
              Enable verbose logging for troubleshooting USPS API requests
            </Form.Text>
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="outline-secondary"
            onClick={() => {
              setShowAddUspsIntegration(false);
              setNewUspsName('');
              setNewUspsClientId('');
              setNewUspsClientSecret('');
              setNewUspsDebug(false);
              setUspsError(null);
              setShowUspsClientSecret(false);
            }}
          >
            Cancel
          </Button>
          <Button
            variant="success"
            onClick={handleCreateUspsIntegration}
            disabled={isCreatingUsps || !newUspsName.trim() || !newUspsClientId.trim() || !newUspsClientSecret.trim()}
          >
            {isCreatingUsps ? (
              <>
                <Spinner animation="border" size="sm" className="me-1" />
                Creating...
              </>
            ) : (
              <>
                <i className="bi bi-plus-circle me-1"></i>
                Create Integration
              </>
            )}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Edit USPS Integration Modal */}
      <Modal show={showEditUspsIntegration} onHide={() => {
        setShowEditUspsIntegration(false);
        setEditingUspsIntegration(null);
        setNewUspsName('');
        setNewUspsClientId('');
        setNewUspsClientSecret('');
        setNewUspsDebug(false);
        setUspsError(null);
        setShowUspsClientSecret(false);
      }} centered>
        <Modal.Header closeButton>
          <Modal.Title>Edit USPS Integration</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {uspsError && (
            <Alert variant="danger" dismissible onClose={() => setUspsError(null)}>
              {uspsError}
            </Alert>
          )}
          <Form.Group className="mb-3">
            <Form.Label>Integration Name <span className="text-danger">*</span></Form.Label>
            <Form.Control
              type="text"
              placeholder="e.g., USPS Production"
              value={newUspsName}
              onChange={(e) => setNewUspsName(e.target.value)}
              maxLength={255}
            />
            <Form.Text className="text-muted">
              Friendly name to identify this integration
            </Form.Text>
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>USPS Client ID <span className="text-danger">*</span></Form.Label>
            <Form.Control
              type="text"
              placeholder="Enter your USPS API Client ID"
              value={newUspsClientId}
              onChange={(e) => setNewUspsClientId(e.target.value)}
              style={{ fontFamily: 'monospace' }}
            />
            <Form.Text className="text-muted">
              Your USPS API Client ID. This will be encrypted and stored securely.
            </Form.Text>
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>USPS Client Secret <span className="text-danger">*</span></Form.Label>
            <div className="input-group">
              <Form.Control
                type={showUspsClientSecret ? "text" : "password"}
                placeholder="Enter your USPS API Client Secret"
                value={newUspsClientSecret}
                onChange={(e) => setNewUspsClientSecret(e.target.value)}
                style={{ fontFamily: 'monospace' }}
              />
              <Button
                variant="outline-secondary"
                onClick={() => setShowUspsClientSecret(!showUspsClientSecret)}
              >
                <i className={`bi ${showUspsClientSecret ? 'bi-eye-slash' : 'bi-eye'}`}></i>
              </Button>
            </div>
            <Form.Text className="text-muted">
              <i className="bi bi-shield-lock me-1"></i>
              Your USPS API Client Secret. This will be encrypted and stored securely.
            </Form.Text>
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Check
              type="checkbox"
              label="Enable Debug Mode"
              checked={newUspsDebug}
              onChange={(e) => setNewUspsDebug(e.target.checked)}
            />
            <Form.Text className="text-muted">
              Enable verbose logging for troubleshooting USPS API requests
            </Form.Text>
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="outline-secondary"
            onClick={() => {
              setShowEditUspsIntegration(false);
              setEditingUspsIntegration(null);
              setNewUspsName('');
              setNewUspsClientId('');
              setNewUspsClientSecret('');
              setNewUspsDebug(false);
              setUspsError(null);
              setShowUspsClientSecret(false);
            }}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleUpdateUspsIntegration}
            disabled={isCreatingUsps || !newUspsName.trim() || !newUspsClientId.trim() || !newUspsClientSecret.trim()}
          >
            {isCreatingUsps ? (
              <>
                <Spinner animation="border" size="sm" className="me-1" />
                Updating...
              </>
            ) : (
              <>
                <i className="bi bi-save me-1"></i>
                Update Integration
              </>
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default USPSTab;
